// src/pages/marketplace/MarketplaceSearchPage.tsx
// Search results page with left sidebar filters and product grid.

import { useEffect, useState, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { observer } from '@react';
import { useProduct, useI18n } from '@react/hooks';
import {
  Button,
  Input,
  Card,
  CardContent,
  Badge,
  ErrorAlert,
} from '@react/components';
import { Select } from '@/components/Select';

/** Render a row of filled/empty stars for a given rating (0-5). */
function StarRating({ rating, size = 'sm' }: { rating: number; size?: 'sm' | 'md' | 'lg' }) {
  const sizeClass = size === 'lg' ? 'w-6 h-6' : size === 'md' ? 'w-5 h-5' : 'w-4 h-4';
  const full = Math.round(rating);
  return (
    <span className="inline-flex items-center gap-0.5" aria-label={`${rating.toFixed(1)} out of 5 stars`}>
      {Array.from({ length: 5 }, (_, i) => (
        <svg
          key={i}
          className={`${sizeClass} ${i < full ? 'text-yellow-400' : 'text-gray-300'}`}
          fill="currentColor"
          viewBox="0 0 20 20"
        >
          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.286 3.957a1 1 0 00.95.69h4.162c.969 0 1.371 1.24.588 1.81l-3.37 2.448a1 1 0 00-.364 1.118l1.287 3.957c.3.921-.755 1.688-1.54 1.118l-3.37-2.448a1 1 0 00-1.176 0l-3.37 2.448c-.784.57-1.838-.197-1.539-1.118l1.287-3.957a1 1 0 00-.364-1.118L2.063 9.384c-.783-.57-.38-1.81.588-1.81h4.162a1 1 0 00.95-.69l1.286-3.957z" />
        </svg>
      ))}
    </span>
  );
}

/** Colored placeholder when product has no icon. */
function ProductIcon({ product }: { product: any }) {
  if (product.icon_url) {
    return (
      <img
        src={product.icon_url}
        alt={product.name}
        className="w-12 h-12 rounded-lg object-cover"
      />
    );
  }
  const bg = product.color_primary || '#6366f1';
  const initial = (product.name ?? '?')[0]?.toUpperCase() ?? '?';
  return (
    <div
      className="w-12 h-12 rounded-lg flex items-center justify-center text-white font-bold text-lg"
      style={{ backgroundColor: bg }}
    >
      {initial}
    </div>
  );
}

type SortOption = 'popularity' | 'rating' | 'newest';

const CAPABILITY_OPTIONS = [
  { value: 'PRODUCT_CAPABILITY_RESOURCE_PROVIDER', label: 'Resource Provider' },
  { value: 'PRODUCT_CAPABILITY_RESOURCE_CONSUMER', label: 'Resource Consumer' },
  { value: 'PRODUCT_CAPABILITY_USER_PROFILES', label: 'User Profiles' },
  { value: 'PRODUCT_CAPABILITY_TEAMS', label: 'Teams' },
  { value: 'PRODUCT_CAPABILITY_BILLING', label: 'Billing' },
  { value: 'PRODUCT_CAPABILITY_SUBSCRIPTIONS', label: 'Subscriptions' },
  { value: 'PRODUCT_CAPABILITY_MARKETPLACE', label: 'Marketplace' },
  { value: 'PRODUCT_CAPABILITY_WEBHOOKS', label: 'Webhooks' },
  { value: 'PRODUCT_CAPABILITY_API', label: 'API' },
  { value: 'PRODUCT_CAPABILITY_EMBEDDABLE', label: 'Embeddable' },
];

export const MarketplaceSearchPage = observer(() => {
  const store = useProduct();
  const { t } = useI18n();
  const [searchParams, setSearchParams] = useSearchParams();

  const query = searchParams.get('q') || '';
  const sortParam = (searchParams.get('sort') || 'popularity') as SortOption;
  const categoryFilter = searchParams.get('category') || '';
  const typeFilter = searchParams.get('type') || 'all'; // 'all' | 'builtin' | 'thirdparty'
  const minRating = parseInt(searchParams.get('min_rating') || '0', 10);
  const cursor = searchParams.get('cursor') || '';

  const [searchInput, setSearchInput] = useState(query);
  const [categories, setCategories] = useState<any[]>([]);
  const [selectedCapabilities, setSelectedCapabilities] = useState<string[]>(
    searchParams.get('capabilities')?.split(',').filter(Boolean) || []
  );

  // Load categories for sidebar
  useEffect(() => {
    store.ListProductCategories().then((res) => {
      const items = res?.categories?.items;
      setCategories(Array.isArray(items) ? items : []);
    }).catch(() => {});
  }, []);

  // Fetch products on filter changes
  useEffect(() => {
    store.ListProducts({
      pagination: { cursor, limit: 20, direction: 'forward' },
      search: query || undefined,
      category: categoryFilter || undefined,
      is_builtin: typeFilter === 'builtin' ? true : typeFilter === 'thirdparty' ? false : undefined,
    });
  }, [query, categoryFilter, typeFilter, minRating, sortParam, cursor]);

  const updateParam = useCallback(
    (key: string, value: string) => {
      const next = new URLSearchParams(searchParams.toString());
      if (value) {
        next.set(key, value);
      } else {
        next.delete(key);
      }
      next.delete('cursor'); // reset pagination on filter change
      setSearchParams(next);
    },
    [searchParams, setSearchParams],
  );

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    updateParam('q', searchInput.trim());
  };

  const handleCapabilityToggle = (cap: string) => {
    const next = selectedCapabilities.includes(cap)
      ? selectedCapabilities.filter((c) => c !== cap)
      : [...selectedCapabilities, cap];
    setSelectedCapabilities(next);
    updateParam('capabilities', next.join(','));
  };

  const handlePageNext = () => {
    const next = new URLSearchParams(searchParams.toString());
    const lastItem = products[products.length - 1];
    if (lastItem) {
      next.set('cursor', lastItem.entity?.id || '');
      setSearchParams(next);
    }
  };

  // Filter and sort locally (the store already did server-side fetch)
  let products: any[] = store.items ?? [];

  // Client-side rating filter
  if (minRating > 0) {
    products = products.filter(
      (p: any) => (p._marketplace?.average_rating ?? 0) >= minRating,
    );
  }

  // Client-side sort
  if (sortParam === 'rating') {
    products = [...products].sort(
      (a, b) => (b._marketplace?.average_rating ?? 0) - (a._marketplace?.average_rating ?? 0),
    );
  } else if (sortParam === 'newest') {
    products = [...products].sort(
      (a, b) =>
        new Date(b.created_at ?? 0).getTime() - new Date(a.created_at ?? 0).getTime(),
    );
  }

  return (
    <div className="space-y-6">
      {/* Search bar */}
      <form onSubmit={handleSearchSubmit} className="flex gap-3">
        <Input
          placeholder={t('marketplace.search')}
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          fullWidth={false}
          className="flex-1"
        />
        <Button type="submit">{t('marketplace.searchButton')}</Button>
      </form>

      {store.error ? <ErrorAlert error={store.error} dismissible onDismiss={() => store.clearError()} /> : null}

      <div className="flex flex-col lg:flex-row gap-8">
        {/* Left sidebar filters */}
        <aside className="w-full lg:w-64 shrink-0 space-y-6">
          {/* Category filter */}
          <div>
            <h3 className="text-sm font-semibold text-foreground mb-3">
              {t('marketplace.filterCategory')}
            </h3>
            <div className="space-y-1">
              <button
                className={`block w-full text-left text-sm px-2 py-1.5 rounded ${
                  !categoryFilter
                    ? 'bg-primary/10 text-primary font-medium'
                    : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                }`}
                onClick={() => updateParam('category', '')}
              >
                {t('marketplace.allCategories')}
              </button>
              {categories.map((cat: any) => (
                <button
                  key={cat.entity?.id || cat.slug}
                  className={`block w-full text-left text-sm px-2 py-1.5 rounded ${
                    categoryFilter === cat.slug
                      ? 'bg-primary/10 text-primary font-medium'
                      : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                  }`}
                  onClick={() => updateParam('category', cat.slug)}
                >
                  {cat.name}
                  {cat.product_count != null && (
                    <span className="ml-1 text-xs opacity-60">({cat.product_count})</span>
                  )}
                </button>
              ))}
            </div>
          </div>

          {/* Rating filter */}
          <div>
            <h3 className="text-sm font-semibold text-foreground mb-3">
              {t('marketplace.filterRating')}
            </h3>
            <div className="space-y-1">
              {[0, 1, 2, 3, 4].map((stars) => (
                <button
                  key={stars}
                  className={`flex items-center gap-2 w-full text-left text-sm px-2 py-1.5 rounded ${
                    minRating === stars
                      ? 'bg-primary/10 text-primary font-medium'
                      : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                  }`}
                  onClick={() => updateParam('min_rating', stars === 0 ? '' : String(stars))}
                >
                  {stars === 0 ? (
                    t('marketplace.anyRating')
                  ) : (
                    <>
                      <StarRating rating={stars} />
                      <span>& {t('marketplace.up')}</span>
                    </>
                  )}
                </button>
              ))}
            </div>
          </div>

          {/* Type filter */}
          <div>
            <h3 className="text-sm font-semibold text-foreground mb-3">
              {t('marketplace.filterType')}
            </h3>
            <div className="space-y-1">
              {[
                { value: 'all', label: t('marketplace.typeAll') },
                { value: 'builtin', label: t('marketplace.typeBuiltin') },
                { value: 'thirdparty', label: t('marketplace.typeThirdParty') },
              ].map((opt) => (
                <button
                  key={opt.value}
                  className={`block w-full text-left text-sm px-2 py-1.5 rounded ${
                    typeFilter === opt.value
                      ? 'bg-primary/10 text-primary font-medium'
                      : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                  }`}
                  onClick={() => updateParam('type', opt.value === 'all' ? '' : opt.value)}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          {/* Capabilities filter */}
          <div>
            <h3 className="text-sm font-semibold text-foreground mb-3">
              {t('marketplace.filterCapabilities')}
            </h3>
            <div className="space-y-2">
              {CAPABILITY_OPTIONS.map((cap) => (
                <label key={cap.value} className="flex items-center gap-2 text-sm cursor-pointer">
                  <input
                    type="checkbox"
                    checked={selectedCapabilities.includes(cap.value)}
                    onChange={() => handleCapabilityToggle(cap.value)}
                    className="rounded border-border text-primary focus:ring-primary"
                  />
                  <span className="text-muted-foreground">{cap.label}</span>
                </label>
              ))}
            </div>
          </div>
        </aside>

        {/* Product results */}
        <div className="flex-1 min-w-0">
          {/* Sort + result count header */}
          <div className="flex items-center justify-between mb-4">
            <p className="text-sm text-muted-foreground">
              {query && (
                <span>
                  {t('marketplace.resultsFor')}{' '}
                  <span className="font-medium text-foreground">"{query}"</span>
                  {' '}
                </span>
              )}
              ({products.length} {t('marketplace.products')})
            </p>
            <Select value={sortParam} onChange={(e) => updateParam('sort', e.target.value)} fullWidth={false}>
              <option value="popularity">{t('marketplace.sortPopularity')}</option>
              <option value="rating">{t('marketplace.sortRating')}</option>
              <option value="newest">{t('marketplace.sortNewest')}</option>
            </Select>
          </div>

          {/* Loading */}
          {store.isLoading ? (
            <div className="flex items-center justify-center h-48">
              <div className="text-muted-foreground">{t('common.loading')}</div>
            </div>
          ) : products.length === 0 ? (
            /* Empty state */
            <Card>
              <CardContent className="py-16 text-center">
                <div className="text-4xl mb-4 text-muted-foreground/40">
                  <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                </div>
                <h3 className="text-lg font-semibold text-foreground mb-2">
                  {t('marketplace.noResults')}
                </h3>
                <p className="text-sm text-muted-foreground mb-4">
                  {t('marketplace.noResultsHint')}
                </p>
                <Link to="/marketplace">
                  <Button variant="secondary">
                    {t('marketplace.backToMarketplace')}
                  </Button>
                </Link>
              </CardContent>
            </Card>
          ) : (
            <>
              {/* Product grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {products.map((product: any) => (
                  <Link
                    key={product.entity?.id || product.slug}
                    to={`/marketplace/${product.slug}`}
                    className="block group"
                  >
                    <Card className="h-full transition-shadow group-hover:shadow-md">
                      <CardContent className="p-5">
                        <div className="flex items-start gap-4 mb-3">
                          <ProductIcon product={product} />
                          <div className="flex-1 min-w-0">
                            <h3 className="text-sm font-semibold text-foreground truncate group-hover:text-primary transition-colors">
                              {product.name}
                            </h3>
                            <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">
                              {product.tagline || product.description}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center justify-between">
                          <StarRating rating={product._marketplace?.average_rating ?? 0} />
                          <div className="flex gap-1">
                            {product.is_builtin && (
                              <Badge variant="default" size="sm">
                                {t('marketplace.builtin')}
                              </Badge>
                            )}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </Link>
                ))}
              </div>

              {/* Pagination */}
              <div className="flex items-center justify-center gap-4 mt-8">
                {cursor && (
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => {
                      const next = new URLSearchParams(searchParams.toString());
                      next.delete('cursor');
                      setSearchParams(next);
                    }}
                  >
                    {t('common.previous')}
                  </Button>
                )}
                {products.length >= 20 && (
                  <Button variant="secondary" size="sm" onClick={handlePageNext}>
                    {t('common.next')}
                  </Button>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
});

export default MarketplaceSearchPage;
